import React, { useState, useCallback, useMemo } from 'react';
import {
  useContentList,
  useContentDetail,
  useContentSummary,
  useContentActions,
  useOwnerCounts,
} from '../hooks/useContentReview';
import { ContentStatus, ContentType, ContentSource } from '../types/content';
import {
  ContentSummaryCards,
  ContentList,
  ContentDetailPanel,
  RejectReasonModal,
} from '../components/content-review';
import type { ContentFilters } from '../components/content-review';
import { CheckCircle, XCircle } from 'lucide-react';
import { cn } from '../utils/helpers';

const MAX_BULK_SELECT = 50;
const PAGE_SIZE = 20;

const ContentReview: React.FC = () => {
  // Selection state
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showDetail, setShowDetail] = useState(false);

  // Filter state
  const [filters, setFilters] = useState<ContentFilters>({
    status: ContentStatus.PENDING_REVIEW,
  });
  const [currentPage, setCurrentPage] = useState(1);

  // Bulk reject modal state
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);

  // Build query params from filters
  const queryParams = useMemo(
    () => ({
      status: filters.status === 'ALL' ? undefined : (filters.status as ContentStatus),
      type: filters.type,
      source: filters.source,
      search: filters.search,
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
    }),
    [filters, currentPage]
  );

  // Hooks
  const {
    items,
    total,
    page,
    totalPages,
    loading: listLoading,
    refetch: refetchList,
  } = useContentList(queryParams);

  const {
    content: selectedContent,
    loading: detailLoading,
    refetch: refetchDetail,
  } = useContentDetail(selectedId);

  const {
    summary,
    loading: summaryLoading,
    refetch: refetchSummary,
  } = useContentSummary();

  const {
    loading: actionLoading,
    regenerateLoading,
    uploadLoading,
    importLoading,
    approve,
    revert,
    reject,
    regenerate,
    bulkApprove,
    bulkReject,
    uploadTikTok,
    importTikTok,
  } = useContentActions();

  const { ownerCounts, refetchOwnerCounts } = useOwnerCounts(filters.status);

  // ---- Handlers ----

  const handleSelectItem = useCallback((id: string) => {
    setSelectedId(id);
    setShowDetail(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setShowDetail(false);
    setSelectedId(null);
  }, []);

  const handleFilterChange = useCallback((newFilters: ContentFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  }, []);

  const handleStatusCardClick = useCallback(
    (status: ContentStatus | 'ALL') => {
      setFilters((prev) => ({ ...prev, status }));
      setCurrentPage(1);
    },
    []
  );

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  // Selection
  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < MAX_BULK_SELECT) {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const newSet = new Set<string>();
    items.slice(0, MAX_BULK_SELECT).forEach((item) => newSet.add(item.id));
    setSelectedIds(newSet);
  }, [items]);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // After a successful status-changing action, clear selection so the item
  // disappears from the detail panel and can't be actioned again.
  const clearSelectionAndRefresh = useCallback(() => {
    setSelectedId(null);
    setShowDetail(false);
    refetchList();
    refetchSummary();
    refetchOwnerCounts();
  }, [refetchList, refetchSummary, refetchOwnerCounts]);

  // Single approve/reject/revert
  const handleApprove = useCallback(
    async (id: string) => {
      const success = await approve(id);
      if (success) clearSelectionAndRefresh();
      return success;
    },
    [approve, clearSelectionAndRefresh]
  );

  const handleRevert = useCallback(
    async (id: string, reason?: string) => {
      const success = await revert(id, reason);
      if (success) clearSelectionAndRefresh();
      return success;
    },
    [revert, clearSelectionAndRefresh]
  );

  const handleReject = useCallback(
    async (id: string, reason?: string) => {
      const success = await reject(id, reason);
      if (success) clearSelectionAndRefresh();
      return success;
    },
    [reject, clearSelectionAndRefresh]
  );

  const handleRegenerate = useCallback(
    async (id: string, prompt?: string) => {
      const success = await regenerate(id, prompt);
      if (success) clearSelectionAndRefresh();
      return success;
    },
    [regenerate, clearSelectionAndRefresh]
  );

  const handleUploadTikTok = useCallback(
    async (tiktokVideoId: string, file: File) => {
      const success = await uploadTikTok(tiktokVideoId, file);
      if (success) clearSelectionAndRefresh();
      return success;
    },
    [uploadTikTok, clearSelectionAndRefresh]
  );

  const handleImportTikTok = useCallback(
    async (tiktokVideoId: string) => {
      const success = await importTikTok(tiktokVideoId);
      if (success) clearSelectionAndRefresh();
      return success;
    },
    [importTikTok, clearSelectionAndRefresh]
  );

  // Bulk approve
  const handleBulkApprove = useCallback(async () => {
    const ids = Array.from(selectedIds);
    const success = await bulkApprove(ids);
    if (success) {
      setSelectedIds(new Set());
      refetchList();
      refetchSummary();
    }
  }, [selectedIds, bulkApprove, refetchList, refetchSummary]);

  // Bulk reject
  const handleBulkReject = useCallback(
    async (reason: string) => {
      const ids = Array.from(selectedIds);
      const success = await bulkReject(ids, reason);
      if (success) {
        setSelectedIds(new Set());
        setShowBulkRejectModal(false);
        refetchList();
        refetchSummary();
      }
    },
    [selectedIds, bulkReject, refetchList, refetchSummary]
  );

  return (
    <div className="h-full flex flex-col">
      {/* Summary Cards */}
      <div className="p-4 border-b border-border flex-shrink-0">
        <ContentSummaryCards
          summary={summary}
          loading={summaryLoading}
          activeStatus={filters.status}
          onStatusClick={handleStatusCardClick}
        />
      </div>

      {/* Two-panel layout */}
      <div className="flex-1 flex min-h-0">
        {/* List Panel */}
        <div
          className={cn(
            'w-full lg:w-96 flex-shrink-0 flex-col',
            showDetail ? 'hidden lg:flex' : 'flex'
          )}
        >
          <ContentList
            items={items}
            loading={listLoading}
            selectedId={selectedId}
            selectedIds={selectedIds}
            filters={filters}
            total={total}
            page={page}
            totalPages={totalPages}
            ownerTotalCounts={ownerCounts}
            onSelectItem={handleSelectItem}
            onToggleSelect={handleToggleSelect}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            onFilterChange={handleFilterChange}
            onPageChange={handlePageChange}
          />
        </div>

        {/* Detail Panel */}
        <div
          className={cn(
            'flex-1 min-w-0',
            showDetail ? 'flex' : 'hidden lg:flex'
          )}
        >
          <ContentDetailPanel
            content={selectedContent}
            loading={detailLoading}
            onApprove={handleApprove}
            onRevert={handleRevert}
            onReject={handleReject}
            onRegenerate={handleRegenerate}
            onUploadTikTok={handleUploadTikTok}
            onImportTikTok={handleImportTikTok}
            onClose={handleCloseDetail}
            actionLoading={actionLoading}
            regenerateLoading={regenerateLoading}
            uploadLoading={uploadLoading}
            importLoading={importLoading}
          />
        </div>
      </div>

      {/* Floating Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-card border border-border shadow-2xl">
            <span className="text-sm font-medium text-foreground">
              {selectedIds.size} selected
            </span>
            <div className="w-px h-6 bg-border" />
            <button
              onClick={() => setShowBulkRejectModal(true)}
              disabled={actionLoading}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium',
                'border border-destructive/30 text-destructive',
                'hover:bg-destructive/10 transition-colors',
                'disabled:opacity-50'
              )}
            >
              <XCircle className="h-4 w-4" />
              Reject
            </button>
            <button
              onClick={handleBulkApprove}
              disabled={actionLoading}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium',
                'bg-emerald-600 text-white',
                'hover:bg-emerald-500 transition-colors',
                'disabled:opacity-50'
              )}
            >
              <CheckCircle className="h-4 w-4" />
              Approve
            </button>
            <button
              onClick={handleDeselectAll}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors ml-1"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Bulk Reject Modal */}
      <RejectReasonModal
        isOpen={showBulkRejectModal}
        itemCount={selectedIds.size}
        onConfirm={handleBulkReject}
        onCancel={() => setShowBulkRejectModal(false)}
        loading={actionLoading}
      />
    </div>
  );
};

export default ContentReview;
